import { useEffect, useRef, useState } from "react";

import { useTokenBroker } from "../../auth/session";
import Button from "../Button";
import Modal from "../Modal";
import {
  type IncomingCall,
  startIncomingCallConnection,
} from "./incomingCallConnection";

// Same-origin in deployed environments, where every app sits behind the application gateway.
const VIDEO_CALL_API_URL: string =
  import.meta.env.VITE_VIDEOCALL_API_URL ??
  `${window.location.origin}/video-call/api`;

const TITLE = "Incoming video call";

const describeCall = (call: IncomingCall) =>
  `${call.callerName || "Support"} is calling about a case on ${call.customer} - ${call.rootEquipment}`;

// A soft two-note ring, repeated. Browsers keep audio suspended until the user has interacted
// with the page, so it can stay silent; the dialog still shows.
function useRingtone(active: boolean) {
  useEffect(() => {
    if (!active || typeof AudioContext === "undefined") return;
    const audioContext = new AudioContext();
    const ring = () => {
      if (audioContext.state !== "running") return;
      [659.25, 523.25].forEach((frequency, i) => {
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        gain.gain.value = 0.15;
        oscillator.frequency.value = frequency;
        oscillator.connect(gain).connect(audioContext.destination);
        const at = audioContext.currentTime + i * 0.4;
        oscillator.start(at);
        oscillator.stop(at + 0.35);
      });
    };
    void audioContext
      .resume()
      .then(ring)
      .catch(() => {});
    const timer = setInterval(ring, 2000);
    return () => {
      clearInterval(timer);
      void audioContext.close().catch(() => {});
    };
  }, [active]);
}

/** Rings the signed-in user when someone invites them into a video call from the web app. */
export default function IncomingCallListener() {
  const broker = useTokenBroker();
  const [call, setCall] = useState<IncomingCall | null>(null);
  const connectionRef = useRef<ReturnType<
    typeof startIncomingCallConnection
  > | null>(null);

  useEffect(() => {
    const connection = startIncomingCallConnection({
      url: `${VIDEO_CALL_API_URL}/support-call/hubs/call-invites`,
      getToken: () => broker.getToken({ background: true }),
      onIncomingCall: setCall,
      onEnded: (ringId) =>
        setCall((current) => (current?.ringId === ringId ? null : current)),
    });
    connectionRef.current = connection;
    return connection.stop;
  }, [broker]);

  useEffect(() => {
    if (!call) return;
    const timer = setTimeout(() => setCall(null), call.expiresInSeconds * 1000);
    return () => clearTimeout(timer);
  }, [call]);

  useRingtone(!!call);

  if (!call) return null;

  const respond = (accepted: boolean) => {
    connectionRef.current?.respond(call.ringId, accepted);
    setCall(null);
  };

  const join = () => {
    const url = `${window.location.origin}/video-call/site/${encodeURIComponent(call.customer)}/call?roomId=${encodeURIComponent(call.ticketNumber)}&rootEquipment=${encodeURIComponent(call.rootEquipment)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    respond(true);
  };

  return (
    // No dismiss on a backdrop click: a stray click shouldn't count as an answer.
    <Modal visible onDismiss={() => {}} centerOnMobile>
      <div
        role="alertdialog"
        aria-label={TITLE}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          maxWidth: "360px",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 500 }}>
          {TITLE}
        </h2>
        <p style={{ margin: 0, fontSize: "14px" }}>{describeCall(call)}</p>
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          <Button
            variant="secondary"
            text="Decline"
            onClick={() => respond(false)}
          />
          <Button variant="primary" text="Join" onClick={join} />
        </div>
      </div>
    </Modal>
  );
}
